import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { toast } from 'sonner';
import api, { cyclesAPI, emailAPI } from '../lib/api';
import { surnameKey } from '../lib/sort';
import { useAuth } from '../context/AuthContext';
import { usePagination, PaginationBar } from '../components/PaginationBar';
import { downloadBlobResponse } from '../components/FeedbackReportDialog';
import {
  ArrowLeft, CalendarRange, ClipboardCheck, AlertTriangle, GraduationCap, Building2, Search,
  ChevronDown, ChevronRight, Eye, Bell, Link2, Download, Clock,
} from 'lucide-react';

const STATE_STYLE = {
  completed: 'bg-blue-100 text-blue-700 border-blue-200',
  current: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  upcoming: 'bg-gray-100 text-gray-600 border-gray-200',
};
const STATE_LABEL = { completed: 'Completed', current: 'In progress', upcoming: 'Upcoming' };

const dotClass = (f) => (f.status === 'submitted' ? 'bg-emerald-500' : f.overdue ? 'bg-red-500' : 'bg-gray-300');

const ProgressStrip = ({ forms, submitted, total }) => (
  <div className="flex items-center gap-2">
    <div className="flex items-center gap-1">
      {forms.map((f) => (
        <span key={f.id} title={`#${f.assessment_number} · ${f.due_date} · ${f.status}${f.overdue ? ' (overdue)' : ''}`}
          className={`w-2.5 h-2.5 rounded-full ${dotClass(f)}`} />
      ))}
      {forms.length === 0 && <span className="text-[11px] text-gray-400 italic">no assessments</span>}
    </div>
    {total > 0 && <span className="text-xs font-semibold text-gray-700">{submitted}/{total}</span>}
  </div>
);

const PhaseDetail = () => {
  const { id, phaseId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, isReadOnly } = useAuth();
  const canWrite = isAdmin && !isReadOnly;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await cyclesAPI.getPhase(id, phaseId);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load phase');
      navigate(`/cycles/${id}`);
    } finally { setLoading(false); }
  }, [id, phaseId, navigate]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.rows || []).filter((r) => {
      if (filter === 'overdue' && r.overdue === 0) return false;
      if (filter === 'complete' && (r.total === 0 || r.submitted < r.total)) return false;
      if (filter === 'pending' && (r.total === 0 || r.submitted >= r.total)) return false;
      if (!q) return true;
      return r.student.name.toLowerCase().includes(q) || (r.student.student_number || '').toLowerCase().includes(q)
        || r.restaurant.name.toLowerCase().includes(q);
    }).sort((a, b) => a.restaurant.name.localeCompare(b.restaurant.name, undefined, { sensitivity: 'base' })
      || surnameKey(a.student.name).localeCompare(surnameKey(b.student.name), undefined, { sensitivity: 'base' }));
  }, [data, search, filter]);
  const pg = usePagination(rows, 'phase-detail');

  const remind = async (formIds, label) => {
    if (formIds.length === 0) { toast.info('Nothing to remind'); return; }
    setBusy(true);
    try {
      const res = await emailAPI.sendReminder({ form_ids: formIds, base_url: window.location.origin });
      if (res.data.sent > 0) toast.success(`${label}: ${res.data.sent} reminder${res.data.sent === 1 ? '' : 's'} sent`);
      if (res.data.errors?.length) toast.error(res.data.errors[0].error || `${res.data.errors.length} failed`);
      load();
    } catch { toast.error('Failed to send reminder'); } finally { setBusy(false); }
  };

  const copyLink = (token) => {
    const url = `${window.location.origin}/feedback/${token}`;
    navigator.clipboard?.writeText(url).then(() => toast.success('Chef link copied'), () => toast.info(url));
  };

  const downloadZip = async () => {
    setBusy(true);
    try {
      const res = await api.get(`/feedback-reports/cycle/${id}/zip`, { params: { phase_id: phaseId }, responseType: 'blob' });
      downloadBlobResponse(res, 'Feedback_Reports.zip');
    } catch (e) {
      let msg = 'No submitted assessments in this phase yet';
      try { msg = JSON.parse(await e.response?.data?.text?.())?.detail || msg; } catch { /* keep */ }
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (loading || !data) {
    return <Layout><div className="p-8 text-gray-500">Loading phase…</div></Layout>;
  }

  const { cycle, phase, stats } = data;
  const pct = stats.assessments_total > 0 ? Math.round((stats.assessments_submitted / stats.assessments_total) * 100) : 0;
  const overdueFormIds = (data.rows || []).flatMap((r) => r.forms.filter((f) => f.overdue).map((f) => f.id));

  return (
    <Layout>
      <div className="space-y-4 animate-fade-in" data-testid={`phase-detail-${phaseId}`}>
        <button data-testid="back-to-cycle" onClick={() => navigate(`/cycles/${id}`)} className="text-xs text-gray-500 hover:text-ica-navy inline-flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Back to {cycle.name}
        </button>

        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="min-w-0">
            <div className="text-[11px] uppercase tracking-wide text-gray-500">{cycle.name} · Phase {phase.index + 1} of {cycle.phases.length}</div>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              <CalendarRange className="w-6 h-6 text-ica-navy" />
              <h1 className="font-heading text-2xl font-bold text-ica-navy truncate" data-testid="phase-title">{phase.name}</h1>
              <span data-testid="phase-state" className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${STATE_STYLE[phase.state]}`}>
                {STATE_LABEL[phase.state]}
              </span>
            </div>
            <p className="text-gray-500 text-sm mt-1">
              <span className="text-gray-800 font-medium">{phase.start_date}</span> → <span className="text-gray-800 font-medium">{phase.end_date}</span>
              {' · '}{phase.num_assessments} assessment{phase.num_assessments === 1 ? '' : 's'} due {(phase.assessment_due_dates || []).join(', ') || '—'}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {cycle.phases.length > 1 && (
              <Select value={phaseId} onValueChange={(v) => navigate(`/cycles/${id}/phases/${v}`)}>
                <SelectTrigger data-testid="phase-switcher" className="h-8 w-56 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cycle.phases.map((p, i) => (
                    <SelectItem key={p.id} value={p.id} data-testid={`phase-switch-${i}`}>Phase {i + 1} · {p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Button data-testid="phase-download-zip" size="sm" variant="outline" onClick={downloadZip} disabled={busy}>
              <Download className="w-3.5 h-3.5 mr-1" /> Phase reports (ZIP)
            </Button>
            {canWrite && overdueFormIds.length > 0 && (
              <Button data-testid="phase-remind-overdue" size="sm" onClick={() => remind(overdueFormIds, 'Overdue')} disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-white">
                <Bell className="w-3.5 h-3.5 mr-1" /> Remind all overdue ({overdueFormIds.length})
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="phase-stats">
          <Card data-testid="phase-stat-submitted">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Assessments submitted</p>
                  <p className="font-heading text-2xl font-bold text-ica-navy">{stats.assessments_submitted}<span className="text-sm text-gray-400 font-medium"> / {stats.assessments_total}</span></p>
                </div>
                <ClipboardCheck className="w-6 h-6 text-ica-navy opacity-60" />
              </div>
              <div className="h-1.5 bg-gray-200 rounded-full mt-2 overflow-hidden">
                <div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} />
              </div>
            </CardContent>
          </Card>
          <Card data-testid="phase-stat-overdue" className={stats.assessments_overdue > 0 ? 'border-red-200 bg-red-50/60' : ''}>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-[11px] uppercase tracking-wide ${stats.assessments_overdue > 0 ? 'text-red-700' : 'text-gray-500'}`}>Overdue</p>
                  <p className={`font-heading text-2xl font-bold ${stats.assessments_overdue > 0 ? 'text-red-600' : 'text-gray-500'}`}>{stats.assessments_overdue}</p>
                </div>
                <AlertTriangle className={`w-6 h-6 opacity-60 ${stats.assessments_overdue > 0 ? 'text-red-600' : 'text-gray-500'}`} />
              </div>
              <p className="text-[11px] text-gray-600 mt-2 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {stats.due_this_week} due in the next 7 days</p>
            </CardContent>
          </Card>
          <Card data-testid="phase-stat-students">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Students in phase</p>
                  <p className="font-heading text-2xl font-bold text-ica-navy">{stats.students}</p>
                </div>
                <GraduationCap className="w-6 h-6 text-ica-navy opacity-60" />
              </div>
              <p className="text-[11px] text-gray-600 mt-2">{stats.students_with_submissions} with at least one submission</p>
            </CardContent>
          </Card>
          <Card data-testid="phase-stat-restaurants">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[11px] uppercase tracking-wide text-gray-500">Restaurants in phase</p>
                  <p className="font-heading text-2xl font-bold text-ica-navy">{stats.restaurants}</p>
                </div>
                <Building2 className="w-6 h-6 text-ica-navy opacity-60" />
              </div>
              <p className="text-[11px] text-gray-600 mt-2">hosting placed students this phase</p>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardContent className="pt-5">
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <div className="relative flex-1 min-w-[200px] max-w-sm">
                <Search className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <Input data-testid="phase-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search student or restaurant…" className="pl-8 h-9" />
              </div>
              <Select value={filter} onValueChange={setFilter}>
                <SelectTrigger data-testid="phase-filter" className="h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All students</SelectItem>
                  <SelectItem value="overdue">With overdue</SelectItem>
                  <SelectItem value="pending">Still pending</SelectItem>
                  <SelectItem value="complete">All submitted</SelectItem>
                </SelectContent>
              </Select>
              <div className="ml-auto flex items-center gap-3 text-[11px] text-gray-500">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> submitted</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-300" /> pending</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> overdue</span>
              </div>
            </div>
            <PaginationBar {...pg.bar} position="top" noun="students" />
            {rows.length === 0 ? (
              <p className="text-sm text-gray-500 py-8 text-center" data-testid="phase-empty">No students match.</p>
            ) : (
              <div className="table-scroll">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-gray-500 border-b">
                      <th className="py-2 pr-2 w-6" />
                      <th className="py-2 pr-4">Student</th>
                      <th className="py-2 pr-4">Restaurant</th>
                      <th className="py-2 pr-4">Phase progress</th>
                      <th className="py-2 pr-4">Next due</th>
                      <th className="py-2 pr-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pg.pageItems.map((r) => {
                      const open = !!expanded[r.placement_id];
                      const pendingIds = r.forms.filter((f) => f.status !== 'submitted').map((f) => f.id);
                      return (
                        <React.Fragment key={r.placement_id}>
                          <tr data-testid={`phase-row-${r.placement_id}`} className="border-b hover:bg-gray-50/60 cursor-pointer" onClick={() => setExpanded((e) => ({ ...e, [r.placement_id]: !open }))}>
                            <td className="py-2.5 pr-2 text-gray-400">{open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}</td>
                            <td className="py-2.5 pr-4">
                              <div className="font-medium text-gray-900">{r.student.name}</div>
                              <div className="text-[11px] text-gray-500">{r.student.student_number}</div>
                            </td>
                            <td className="py-2.5 pr-4 text-gray-700">{r.restaurant.name}</td>
                            <td className="py-2.5 pr-4"><ProgressStrip forms={r.forms} submitted={r.submitted} total={r.total} /></td>
                            <td className="py-2.5 pr-4">
                              {r.next_due ? (
                                <span className={`text-xs ${r.overdue > 0 ? 'text-red-600 font-semibold' : 'text-gray-700'}`}>{r.next_due}{r.overdue > 0 ? ` · ${r.overdue} overdue` : ''}</span>
                              ) : r.total > 0 ? <span className="text-xs text-emerald-700 font-medium">All submitted</span> : <span className="text-xs text-gray-400">—</span>}
                            </td>
                            <td className="py-2.5 pr-2 text-right" onClick={(e) => e.stopPropagation()}>
                              {canWrite && pendingIds.length > 0 && (
                                <button data-testid={`phase-remind-${r.placement_id}`} onClick={() => remind(pendingIds, r.student.name)} className="p-1.5 hover:bg-amber-50 rounded text-amber-600" title="Remind chef about pending assessments">
                                  <Bell className="w-4 h-4" />
                                </button>
                              )}
                            </td>
                          </tr>
                          {open && (
                            <tr className="bg-gray-50/70 border-b" data-testid={`phase-row-forms-${r.placement_id}`}>
                              <td />
                              <td colSpan={5} className="py-2 pr-2">
                                {r.forms.length === 0 ? (
                                  <p className="text-xs text-gray-500 italic py-1">No assessments scheduled for this student in this phase.</p>
                                ) : (
                                  <div className="divide-y divide-gray-200">
                                    {r.forms.map((f) => (
                                      <div key={f.id} className="flex items-center gap-3 py-1.5 text-xs" data-testid={`phase-form-${f.id}`}>
                                        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${dotClass(f)}`} />
                                        <span className="font-semibold text-gray-800 w-28">Assessment #{f.assessment_number}</span>
                                        <span className="text-gray-600 w-28">Due {f.due_date}</span>
                                        <span className={`px-1.5 py-0.5 rounded font-medium ${f.status === 'submitted' ? 'bg-emerald-100 text-emerald-700' : f.overdue ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700'}`}>
                                          {f.status === 'submitted' ? `Submitted ${(f.submitted_at || '').slice(0, 10)}` : f.overdue ? 'Overdue' : 'Pending'}
                                        </span>
                                        {f.reminder_sent_at && f.status !== 'submitted' && <span className="text-gray-400">reminded {f.reminder_sent_at.slice(0, 10)}</span>}
                                        <span className="ml-auto flex items-center gap-1">
                                          {f.status === 'submitted' ? (
                                            <button data-testid={`phase-view-${f.id}`} onClick={() => navigate(`/feedback-view/${f.id}`)} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="View submitted feedback"><Eye className="w-4 h-4" /></button>
                                          ) : (
                                            <>
                                              <button data-testid={`phase-open-form-${f.id}`} onClick={() => window.open(`/feedback/${f.access_token}`, '_blank')} className="p-1.5 hover:bg-blue-50 rounded text-blue-600" title="Open the assessment form"><Eye className="w-4 h-4" /></button>
                                              <button data-testid={`phase-copy-${f.id}`} onClick={() => copyLink(f.access_token)} className="p-1.5 hover:bg-gray-100 rounded text-gray-600" title="Copy chef link"><Link2 className="w-4 h-4" /></button>
                                              {canWrite && <button data-testid={`phase-remind-form-${f.id}`} onClick={() => remind([f.id], `#${f.assessment_number}`)} className="p-1.5 hover:bg-amber-50 rounded text-amber-600" title="Send reminder to chef"><Bell className="w-4 h-4" /></button>}
                                            </>
                                          )}
                                        </span>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            <PaginationBar {...pg.bar} position="bottom" noun="students" />
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PhaseDetail;
